import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Save, Boxes, ListChecks, Package, FileText, Plus, Trash2, GripVertical } from "lucide-react";
import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  closestCenter,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import { reorderWithOrderField } from "@/lib/reorder";

import { api, formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import Breadcrumbs from "@/components/Breadcrumbs";
import FileUploader from "@/components/FileUploader";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const CHECK_KINDS = [
  { value: "yes_no", label: "Yes/No" },
  { value: "ok_issue", label: "OK/Issue" },
  { value: "numeric", label: "Numeric" },
  { value: "text", label: "Text" },
];

function uid() {
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

const EMPTY = {
  name: "",
  description: "",
  default_checklist: [],
  default_parts_picklist: [],
  manual_file_ids: [],
  active: true,
};

export default function MachineTypeForm() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();

  const [form, setForm] = useState(EMPTY);
  const [manuals, setManuals] = useState([]);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const { data } = await api.get(`/machine-types/${id}`);
        setForm({
          name: data.name || "",
          description: data.description || "",
          default_checklist: data.default_checklist || [],
          default_parts_picklist: data.default_parts_picklist || [],
          manual_file_ids: data.manual_file_ids || [],
          active: data.active,
        });
        setManuals(data.manuals || []);
      } catch (err) {
        toast.error(formatApiError(err));
        navigate("/machine-types", { replace: true });
      } finally { setLoading(false); }
    })();
  }, [id, isEdit, navigate]);

  const update = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  // Checklist ops
  const addChecklist = () => update({
    default_checklist: [
      ...form.default_checklist,
      { id: uid(), label: "", kind: "yes_no", required: false, order: form.default_checklist.length },
    ],
  });
  const removeChecklist = (idx) => update({
    default_checklist: form.default_checklist
      .filter((_, i) => i !== idx)
      .map((it, i) => ({ ...it, order: i })),
  });
  const updateChecklist = (idx, patch) => update({ default_checklist: form.default_checklist.map((it, i) => (i === idx ? { ...it, ...patch } : it)) });
  const onChecklistDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const oldIndex = form.default_checklist.findIndex((i) => i.id === active.id);
    const newIndex = form.default_checklist.findIndex((i) => i.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    update({ default_checklist: reorderWithOrderField(form.default_checklist, oldIndex, newIndex) });
  };

  // Parts ops
  const addPart = () => update({ default_parts_picklist: [...form.default_parts_picklist, { id: uid(), sku: "", name: "", unit: "ea" }] });
  const removePart = (idx) => update({ default_parts_picklist: form.default_parts_picklist.filter((_, i) => i !== idx) });
  const updatePart = (idx, patch) => update({ default_parts_picklist: form.default_parts_picklist.map((it, i) => (i === idx ? { ...it, ...patch } : it)) });

  // Manuals
  const handleAttach = async (file) => {
    if (!isEdit) {
      update({ manual_file_ids: [...form.manual_file_ids, file.id] });
      setManuals([...manuals, file]);
      return;
    }
    const { data } = await api.post(`/machine-types/${id}/manuals`, { file_id: file.id });
    update({ manual_file_ids: data.manual_file_ids });
    setManuals(data.manuals);
  };
  const handleDetach = async (fileId) => {
    if (!isEdit) {
      update({ manual_file_ids: form.manual_file_ids.filter((f) => f !== fileId) });
      setManuals(manuals.filter((m) => m.id !== fileId));
      return;
    }
    const { data } = await api.delete(`/machine-types/${id}/manuals/${fileId}`);
    update({ manual_file_ids: data.manual_file_ids });
    setManuals(data.manuals);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      if (isEdit) {
        const { data } = await api.patch(`/machine-types/${id}`, form);
        toast.success("Machine type updated");
        navigate(`/machine-types/${data.id}`);
      } else {
        const { data } = await api.post("/machine-types", form);
        toast.success("Machine type created");
        navigate(`/machine-types/${data.id}`);
      }
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  if (loading) return <div className="p-6 text-sm text-muted-foreground">Loading…</div>;

  return (
    <div className="p-6 space-y-6" data-testid="machine-type-form-page">
      <Breadcrumbs
        items={[
          { label: "Settings" },
          { label: "Machine Types", to: "/machine-types" },
          ...(isEdit ? [{ label: form.name || "Type", to: `/machine-types/${id}` }, { label: "Edit" }] : [{ label: "New" }]),
        ]}
      />
      <PageHeader
        testId="machine-type-form-header"
        title={isEdit ? `Edit ${form.name}` : "New machine type"}
        actions={<Button asChild variant="outline" size="sm" data-testid="machine-type-form-back"><Link to="/machine-types"><ArrowLeft className="h-4 w-4" /> Back</Link></Button>}
      />

      <form onSubmit={submit} className="space-y-6 max-w-4xl">
        <Card className="rounded-md">
          <CardHeader><CardTitle className="text-sm font-medium flex items-center gap-2"><Boxes className="h-4 w-4 text-primary" /> Details</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5 md:col-span-2">
              <Label htmlFor="name">Name</Label>
              <Input id="name" value={form.name} onChange={(e) => update({ name: e.target.value })} required data-testid="machine-type-form-name" />
            </div>
            <div className="space-y-1.5 md:col-span-2">
              <Label htmlFor="desc">Description</Label>
              <Textarea id="desc" rows={3} value={form.description} onChange={(e) => update({ description: e.target.value })} data-testid="machine-type-form-description" />
            </div>
            <div className="md:col-span-2 flex items-center justify-between rounded-sm border border-border bg-card p-3">
              <div>
                <div className="text-sm font-medium">Active</div>
                <div className="text-xs text-muted-foreground">Inactive types are hidden from selectors.</div>
              </div>
              <Switch checked={form.active} onCheckedChange={(v) => update({ active: v })} data-testid="machine-type-form-active" />
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-md">
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle className="text-sm font-medium flex items-center gap-2"><ListChecks className="h-4 w-4 text-primary" /> Default checklist</CardTitle>
            <Button type="button" size="sm" variant="outline" onClick={addChecklist} data-testid="machine-type-form-add-checklist">
              <Plus className="h-3.5 w-3.5" /> Add item
            </Button>
          </CardHeader>
          <CardContent>
            {form.default_checklist.length === 0 ? (
              <div className="py-6 text-center text-xs text-muted-foreground" data-testid="machine-type-checklist-empty">No items yet.</div>
            ) : (
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onChecklistDragEnd}>
                <SortableContext items={form.default_checklist.map((i) => i.id)} strategy={verticalListSortingStrategy}>
                  <ul className="space-y-2">
                    {form.default_checklist.map((item, idx) => (
                      <SortableChecklistRow
                        key={item.id}
                        item={item}
                        idx={idx}
                        onChange={(patch) => updateChecklist(idx, patch)}
                        onRemove={() => removeChecklist(idx)}
                      />
                    ))}
                  </ul>
                </SortableContext>
              </DndContext>
            )}
          </CardContent>
        </Card>

        <Card className="rounded-md">
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle className="text-sm font-medium flex items-center gap-2"><Package className="h-4 w-4 text-primary" /> Default parts picklist</CardTitle>
            <Button type="button" size="sm" variant="outline" onClick={addPart} data-testid="machine-type-form-add-part">
              <Plus className="h-3.5 w-3.5" /> Add part
            </Button>
          </CardHeader>
          <CardContent>
            {form.default_parts_picklist.length === 0 ? (
              <div className="py-6 text-center text-xs text-muted-foreground" data-testid="machine-type-parts-empty">No parts yet.</div>
            ) : (
              <ul className="space-y-2">
                {form.default_parts_picklist.map((p, idx) => (
                  <li key={p.id} data-testid={`machine-type-part-${idx}`} className="grid grid-cols-12 gap-2 items-center border border-border rounded-sm p-2 bg-card">
                    <Input placeholder="SKU" value={p.sku} onChange={(e) => updatePart(idx, { sku: e.target.value })} className="col-span-3 h-9" data-testid={`machine-type-part-${idx}-sku`} />
                    <Input placeholder="Name" value={p.name} onChange={(e) => updatePart(idx, { name: e.target.value })} className="col-span-6 h-9" data-testid={`machine-type-part-${idx}-name`} />
                    <Input placeholder="Unit" value={p.unit} onChange={(e) => updatePart(idx, { unit: e.target.value })} className="col-span-2 h-9" data-testid={`machine-type-part-${idx}-unit`} />
                    <Button type="button" variant="ghost" size="icon" className="col-span-1 h-8 w-8" onClick={() => removePart(idx)} data-testid={`machine-type-part-${idx}-remove`}><Trash2 className="h-4 w-4" /></Button>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        <Card className="rounded-md">
          <CardHeader><CardTitle className="text-sm font-medium flex items-center gap-2"><FileText className="h-4 w-4 text-primary" /> Manuals</CardTitle></CardHeader>
          <CardContent>
            <FileUploader files={manuals} onAttach={handleAttach} onDetach={handleDetach} testIdPrefix="machine-type-manuals" />
          </CardContent>
        </Card>

        <div className="flex items-center justify-end gap-2">
          <Button type="button" variant="outline" onClick={() => navigate("/machine-types")} data-testid="machine-type-form-cancel">Cancel</Button>
          <Button type="submit" disabled={saving} data-testid="machine-type-form-submit"><Save className="h-4 w-4" />{saving ? "Saving…" : isEdit ? "Save changes" : "Create type"}</Button>
        </div>
      </form>
    </div>
  );
}

function SortableChecklistRow({ item, idx, onChange, onRemove }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.7 : 1 };
  return (
    <li
      ref={setNodeRef}
      style={style}
      data-testid={`machine-type-checklist-${idx}`}
      className="grid grid-cols-12 gap-2 items-center border border-border rounded-sm p-2 bg-card"
    >
      <button
        type="button"
        {...attributes}
        {...listeners}
        className="col-span-1 h-8 flex items-center justify-center text-muted-foreground hover:text-foreground cursor-grab active:cursor-grabbing"
        data-testid={`machine-type-checklist-${idx}-drag`}
        aria-label="Drag to reorder"
      >
        <GripVertical className="h-4 w-4" />
      </button>
      <Input value={item.label} onChange={(e) => onChange({ label: e.target.value })} placeholder="Checklist item label" className="col-span-6 h-9" data-testid={`machine-type-checklist-${idx}-label`} />
      <Select value={item.kind} onValueChange={(v) => onChange({ kind: v })}>
        <SelectTrigger className="col-span-3 h-9 text-xs" data-testid={`machine-type-checklist-${idx}-kind`}><SelectValue /></SelectTrigger>
        <SelectContent>
          {CHECK_KINDS.map((k) => (<SelectItem key={k.value} value={k.value}>{k.label}</SelectItem>))}
        </SelectContent>
      </Select>
      <label className="col-span-1 flex items-center justify-center text-xs gap-1" data-testid={`machine-type-checklist-${idx}-required-wrap`}>
        <Switch checked={item.required} onCheckedChange={(v) => onChange({ required: v })} data-testid={`machine-type-checklist-${idx}-required`} />
      </label>
      <Button type="button" variant="ghost" size="icon" className="col-span-1 h-8 w-8" onClick={onRemove} data-testid={`machine-type-checklist-${idx}-remove`}><Trash2 className="h-4 w-4" /></Button>
    </li>
  );
}
